// services/tenantManagementService.ts
//
// Super Admin — Tenant Management service wrappers.
//
// URL prefix is /super-admin/tenants per the PF-2 BE re-mount. The legacy
// /tenants routes stay live for one release as a fallback; do not target
// them from new code.

import { api } from '@edemics/lib/api'

const BASE = '/super-admin/tenants'

export type TenantStatus = 'Active' | 'Disabled' | 'ExpiringSoon' | 'Expired'

export interface Tenant {
  id: string
  /**
   * Entity-prefixed display code (e.g. `TNT-K3M2P9AB`) from BE PR #20.
   * Nullable — legacy rows before the backfill may not have one, so the
   * FE renders `record.code ?? record.id`.
   */
  code?: string | null
  name: string
  subdomain: string
  customDomain: string | null
  isActive: boolean
  organizationType: string | null
  country: string | null
  timezone: string | null
  contactEmail: string | null
  contactPhone: string | null
  emailCC: string | null
  subscriptionPlanId: string | null
  subscriptionCycle: 'Annual' | 'Monthly' | null
  subscriptionStatus: TenantStatus
  activatedAt: string | null
  renewalDate: string | null
  gracePeriodDays: number
  effectiveRenewal: string | null
  storageAllocatedGb: number
  storageUsedBytes: string   // BigInt over the wire
  lastBackupAt: string | null
  usersCount: number
  provisioningState: 'Pending' | 'Provisioned' | 'Failed'
  provisioningError: string | null
  adminEmail: string | null
  adminFirstName: string | null
  adminLastName: string | null
  /** P1 (V5 pg21 tenant-mgt/view) — logo URL after upload, null on legacy rows. */
  logoUrl?: string | null
  createdAt: string
  updatedAt: string
  /** Wave-2: resolved subscription plan name for this tenant. */
  planName?: string | null
  /**
   * H4 (2026-09-03): resolved subscription plan tier — 'Basic' |
   * 'Standard' | 'Premium'. Null when the tenant has no
   * subscriptionPlanId yet. Surfaced so the super-admin TenantViewPage
   * can render a plan-tier pill next to planName.
   */
  planTier?: string | null
  /** Wave-2 — detail-view companion projections. Populated only on getTenant(). */
  billingTotals?: {
    paid: number
    outstanding: number
    overdue: number
  }
  attachments?: unknown[]
  licensing?: {
    storageAllocatedGb?: number
    storageUsedBytes?: string
    sms?: null | { allocated: number; used: number }
  }
  compliance?: {
    certificateOfIncorporationUrl?: string | null
    signedIndemnityFormUrl?: string | null
    scumlCertificateUrl?: string | null
    tinCertificateUrl?: string | null
    letterOfIntentUrl?: string | null
  }
  /**
   * ACC-BUGS V5 (2026-09-21) — payment flow signals. `lastPaymentMethod`
   * echoes what the wizard captured; `bankTransferConfirmedAt` is null
   * while a bank transfer is pending confirmation and stamps once the
   * super-admin clicks "Payment Confirmed". `isTrial` + `trialEndsAt`
   * flag trial tenants — the FE hides payment UI for those.
   */
  lastPaymentMethod?: 'paystack' | 'flutterwave' | 'gtsquad' | 'bank' | null
  lastPaymentReference?: string | null
  bankTransferEvidenceUrl?: string | null
  bankTransferConfirmedAt?: string | null
  isTrial?: boolean
  trialEndsAt?: string | null
}

export interface CreateTenantWizardPayload {
  name: string
  subdomain: string
  customDomain?: string
  sslCertificateUrl?: string
  sslPrivateKeyUrl?: string
  organizationType?: string
  country?: string
  timezone?: string
  contactEmail?: string
  contactPhone?: string
  emailCC?: string
  subscriptionPlanId?: string
  subscriptionCycle?: 'Annual' | 'Monthly'
  lastPaymentMethod?: 'paystack' | 'flutterwave' | 'gtsquad' | 'bank'
  lastPaymentReference?: string
  bankTransferEvidenceUrl?: string
  /**
   * ACC-BUGS V5 (2026-09-21) — super-admin has already sighted the
   * bank transfer landing in the platform account and wants to skip
   * the 48h hold. Sets `bankTransferConfirmedAt` server-side.
   */
  paymentConfirmed?: boolean
  /**
   * ACC-BUGS V5 (2026-09-21) — trial provisioning. When enabled the
   * tenant is Active immediately with `renewalDate = now + days` and
   * no payment method required. Days capped server-side at 90.
   */
  trial?: {
    enabled: boolean
    days?: number
  }
  adminEmail: string
  adminFirstName?: string
  adminLastName?: string
  adminPassword?: string
}

// ────────────────────────────────────────────────────────────────────────────
// READS
// ────────────────────────────────────────────────────────────────────────────

export async function listTenants(params: { search?: string; planId?: string; status?: TenantStatus } = {}) {
  const { data } = await api.get<Tenant[]>(BASE, { params })
  return data
}

export async function getTenant(codeOrId: string) {
  const { data } = await api.get<Tenant>(`${BASE}/${encodeURIComponent(codeOrId)}`)
  return data
}

// ────────────────────────────────────────────────────────────────────────────
// AVAILABILITY CHECKS
// ────────────────────────────────────────────────────────────────────────────

export interface AvailabilityResult {
  available: boolean
  reason?: 'reserved' | 'taken' | 'primary-only' | 'invalid-format'
  message?: string
}

/**
 * Public tenant summary — the narrow shape returned by
 * `/tenants/lookup-by-subdomain/:subdomain` (R1 BE, wired unauth so it can
 * boot the FE before a JWT exists). Everything sensitive
 * (plan, billing, licensing, etc.) stays behind auth on the super-admin
 * endpoints; this projection is safe to hand any anonymous visitor who
 * hit the tenant's subdomain.
 */
export interface TenantSummary {
  id: string
  code?: string | null
  name: string
  subdomain: string
  customDomain: string | null
  isActive: boolean
  logoUrl?: string | null
  timezone: string | null
  country: string | null
}

/**
 * Resolve a tenant by its subdomain slug. Called at boot by TenantProvider
 * so every service call afterwards knows whose tenant they're in.
 *
 * BE contract:
 *   GET /tenants/lookup-by-subdomain/:subdomain
 *   200 → TenantSummary   (public projection, no auth required)
 *   404 → tenant not found / disabled — caller falls back to apex mode
 *
 * R2 ships the FE side ahead of R1 (BE endpoint). If the endpoint 404s
 * (or the BE isn't there yet), TenantProvider treats it as "unknown
 * subdomain" and boots into apex mode gracefully. Callers should
 * therefore NEVER assume this resolves — always handle the reject path.
 */
export async function lookupTenantBySubdomain(subdomain: string): Promise<TenantSummary> {
  const { data } = await api.get<TenantSummary>(
    `/tenants/lookup-by-subdomain/${encodeURIComponent(subdomain)}`,
  )
  return data
}

/**
 * Wave S (2026-09-10) — resolve a tenant by any live hostname.
 *
 * The subdomain-only lookup was the R1 shape; with BYO custom domains
 * a tenant on `portal.acme.edu` doesn't have a subdomain to look up.
 * TenantProvider prefers this over `lookupTenantBySubdomain` when the
 * host isn't on `.edemics.com`.
 *
 * BE contract:
 *   GET /tenants/lookup-by-hostname/:hostname
 *   200 → TenantSummary (+ optional customDomain field)
 *   404 → not found / disabled / mid-provisioning custom domain
 */
export async function lookupTenantByHostname(hostname: string): Promise<TenantSummary> {
  const { data } = await api.get<TenantSummary>(
    `/tenants/lookup-by-hostname/${encodeURIComponent(hostname)}`,
  )
  return data
}

export async function checkSubdomain(subdomain: string): Promise<AvailabilityResult> {
  const { data } = await api.post<AvailabilityResult>(`${BASE}/check-subdomain`, {
    subdomain: subdomain.toLowerCase().trim(),
  })
  return data
}

export async function checkCustomDomain(domain: string): Promise<AvailabilityResult> {
  const { data } = await api.post<AvailabilityResult>(`${BASE}/check-custom-domain`, {
    domain: domain.toLowerCase().trim(),
  })
  return data
}

// ────────────────────────────────────────────────────────────────────────────
// WIZARD CREATE
// ────────────────────────────────────────────────────────────────────────────

export interface CreateTenantWizardResponse {
  tenant: Tenant
  adminUser: {
    id: string
    email: string
    name: string
    role: string
    generatedPassword?: string
  }
  pendingBankConfirmation: boolean
  /**
   * ACC-BUGS V5 (2026-09-21) — true when the wizard was submitted with
   * `trial.enabled=true`. The success screen renders the trial banner
   * (days remaining, no payment shown).
   */
  isTrial?: boolean
  trialEndsAt?: string | null
  /**
   * ACC-BUGS V5 (2026-09-13) — populated when the wizard supplied a
   * `customDomain`. The success screen renders CNAME instructions so
   * the operator can hand the tenant admin exactly what DNS record to
   * add. Cloudflare issues Universal SSL once DNS resolves — no cert
   * upload required.
   */
  customDomainInstructions?: {
    hostname: string
    verificationTarget: string
    status: string
  } | null
}

export async function createTenantFromWizard(payload: CreateTenantWizardPayload) {
  const { data } = await api.post<CreateTenantWizardResponse>(`${BASE}/wizard`, payload)
  return data
}

// ────────────────────────────────────────────────────────────────────────────
// ACC-BUGS V5 (2026-09-21) — Payment confirmation + credentials resend.
//
// `confirmTenantPayment` flips a pending bank-transfer tenant into
// fully-active. Used from the TenantViewPage action menu after the
// operator sights the transfer in the bank statement.
//
// `sendTenantAdminCredentials` regenerates the admin password and emails
// it via ACS. Used both immediately after payment confirmation and any
// time the admin loses their credentials. The response includes the
// plaintext password so the operator can also read it off-screen and
// hand it over manually if ACS is down.
// ────────────────────────────────────────────────────────────────────────────

export interface ConfirmPaymentResponse {
  ok: boolean
  alreadyConfirmed?: boolean
  confirmedAt?: string
  renewalDate?: string | null
}

export async function confirmTenantPayment(id: string) {
  const { data } = await api.post<ConfirmPaymentResponse>(`${BASE}/${id}/confirm-payment`)
  return data
}

export interface SendAdminCredentialsResponse {
  ok: boolean
  recipient: string
  loginUrl: string
  generatedPassword: string
  emailSent: boolean
  emailError: string | null
}

export async function sendTenantAdminCredentials(id: string) {
  const { data } = await api.post<SendAdminCredentialsResponse>(
    `${BASE}/${id}/send-admin-credentials`,
  )
  return data
}

// ────────────────────────────────────────────────────────────────────────────
// LIFECYCLE OPERATIONS
// ────────────────────────────────────────────────────────────────────────────

export async function updateTenant(id: string, patch: Partial<Tenant>) {
  const { data } = await api.patch<Tenant>(`${BASE}/${id}`, patch)
  return data
}

export async function disableTenant(id: string) {
  const { data } = await api.post<Tenant>(`${BASE}/${id}/disable`)
  return data
}

export async function enableTenant(id: string) {
  const { data } = await api.post<Tenant>(`${BASE}/${id}/enable`)
  return data
}

export async function addGracePeriod(id: string, days: number) {
  const { data } = await api.post<Tenant & { effectiveRenewalDate: string | null }>(
    `${BASE}/${id}/grace-period`,
    { days },
  )
  return data
}

export async function renewTenant(id: string, payload: {
  subscriptionPlanId?: string
  subscriptionCycle?: 'Annual' | 'Monthly'
  paymentMethod?: 'paystack' | 'flutterwave' | 'gtsquad' | 'bank'
  paymentReference?: string
}) {
  const { data } = await api.post<Tenant>(`${BASE}/${id}/renew`, payload)
  return data
}

export async function upgradeTenant(id: string, payload: {
  subscriptionPlanId: string
  subscriptionCycle?: 'Annual' | 'Monthly'
  paymentMethod?: 'paystack' | 'flutterwave' | 'gtsquad' | 'bank'
  paymentReference?: string
}) {
  const { data } = await api.post<Tenant>(`${BASE}/${id}/upgrade`, payload)
  return data
}

export async function downgradeTenant(id: string, payload: {
  subscriptionPlanId: string
  subscriptionCycle?: 'Annual' | 'Monthly'
}) {
  const { data } = await api.post<Tenant>(`${BASE}/${id}/downgrade`, payload)
  return data
}

export async function sendRenewalMessage(id: string) {
  const { data } = await api.post<{ ok: boolean; recipient: string | null }>(
    `${BASE}/${id}/send-renewal-message`,
  )
  return data
}

// ─── Super-admin tenant users (2026-09-10 hotfix) ───────────────

export interface TenantUserRow {
  id: string
  name: string
  email: string
  role: string
  enabled: boolean
  avatar: string | null
  lastLoginAt: string | null
  mustChangePasswordAt: string | null
  isAdmin: boolean
}

export interface TenantUsersResponse {
  adminUserId: string | null
  users: TenantUserRow[]
}

/** List every user of a tenant. Powers the Users tab on the super-admin
 *  TenantViewPage. */
export async function listTenantUsers(tenantId: string): Promise<TenantUsersResponse> {
  const { data } = await api.get<TenantUsersResponse>(`${BASE}/${tenantId}/users`)
  return data
}

/** Reset a specific tenant user's password. Distinct from the tenant-
 *  scoped user-management endpoint because super-admins have kind=
 *  'platform' and no tenantId on their JWT. The user is stamped with
 *  mustChangePasswordAt=now so they're forced through the change-
 *  password flow on next login. */
export async function resetTenantUserPassword(
  tenantId: string,
  userId: string,
  newPassword: string,
): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>(
    `${BASE}/${tenantId}/users/${userId}/reset-password`,
    { newPassword },
  )
  return data
}
